import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppProvider from './AppProvider';
import GoogleVerification from '../components/GoogleVerification';
import LandingPage from '../components/LandingPage/LandingPage';
import { LoadingView } from '../components/LoadingView';
import { useAuthSessionState } from './AuthSessionState';
import FindALawyer from '../views/FindALawyer/FindALawyer';
import SignEngagementLetter from '../views/SignEngagementLetter/SignEngagementLetter';
import RegisterForm from '@/views/EmployeeAuth/RegisterForm';
import EmailPasswordLoginForm from '@/views/EmployeeAuth/LoginForm';
import { AuthComplete } from '@/views/EmployeeAuth/AuthComplete';
import ScreenViewReporter from '@/components/ScreenViewReporter';
import UnauthenticatedAppLayout from '@/app/UnauthenticatedAppLayout';
import ForgotPasswordForm from '@/views/EmployeeAuth/ForgotPasswordForm';
import AuthenticatedAppLayout from './AuthenticatedAppLayout';
import MsalAuthRedirect from '@/components/MsalAuthRedirect';
import { DevPanel } from '../components/DevPanel/DevPanel';
import GoogleAuthRedirect from '@/components/GoogleAuthRedirect';
import { DocumentTitleProvider } from '@/contexts/DocumentTitleContext';
import { useDocumentTitle } from '@/app/useDocumentTitle';
import { FirmsBrowserRouter } from './FirmsBrowserRouter';
const SavedPaymentMethodRedirect = lazy(
  () =>
    import('@/views/PractistAccount/components/AddFirmToPractistPaymentMethod/SavedPaymentMethodRedirect'),
);
const PrivacyPolicy = lazy(
  () => import('../views/PrivacyPolicy/PrivacyPolicy'),
);

function AppContent() {
  useDocumentTitle();

  return (
    <>
      {/* Note: Can't move FirebaseAnalytics to AppProvider because it needs the router context */}
      <ScreenViewReporter />

      <Routes>
        {/*
            1) Engagement Letters: Unauthenticated routes
            (Need to be accessible to non-logged-in users)
          */}
        <Route
          path='/engagement/:letterId/:matterId/:formattedName/:firmId'
          element={<SignEngagementLetter />}
        />
        <Route path='/findalawyer' element={<FindALawyer />} />

        <Route
          path='/privacy'
          element={
            <Suspense fallback={<LoadingView />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path='/google9db2b2215cb7590d.html'
          element={<GoogleVerification />}
        />
        <Route
          path='/hostedaccountcreationredirect'
          element={
            <Suspense
              fallback={
                <div
                  style={{
                    // This color matches the background color of the
                    // payment form, which is where this fallback view will
                    // be displayed.
                    backgroundColor: 'var(--mantine-color-gray-0)',
                    width: '100%',
                    height: '100%',
                  }}
                ></div>
              }
            >
              <SavedPaymentMethodRedirect />
            </Suspense>
          }
        />
        <Route path='/msal-auth-redirect' element={<MsalAuthRedirect />} />
        <Route path='/google-auth-redirect' element={<GoogleAuthRedirect />} />
        <Route path='/auth/complete' element={<AuthComplete />} />
        <Route path='/' element={<LandingPageOrMatters />} />
        <Route element={<UnauthenticatedAppLayout />}>
          <Route path='register' element={<RegisterForm />} />
          <Route path='login' element={<EmailPasswordLoginForm />} />
          <Route path='forgot-password' element={<ForgotPasswordForm />} />
        </Route>
        <Route path='*' element={<AuthenticatedAppLayout />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AppProvider>
      <DevPanel />
      <FirmsBrowserRouter>
        <DocumentTitleProvider>
          <AppContent />
        </DocumentTitleProvider>
      </FirmsBrowserRouter>
    </AppProvider>
  );
}

export default App;

const LandingPageOrMatters = () => {
  const authState = useAuthSessionState();
  if (authState.status === 'authenticated') {
    return <Navigate to='/matters' replace />;
  }
  return <LandingPage />;
};
